import { Typography, Chip, Paper, Checkbox, IconButton } from "@mui/material";
import { CallSplit } from "@mui/icons-material";
import { Draggable } from "@hello-pangea/dnd";
import type { GrowerInfo } from "./types";
import { makeGrowerDraggableId } from "./draggableId.helpers";
import { PortalTooltip } from "../../../../../../helpers";

export function GrowerCard({
	grower,
	index,
	isSelected,
	onToggleSelect,
	multiDragCount = 0,
	onExplode,
}: {
	grower: GrowerInfo;
	index: number;
	isSelected?: boolean;
	onToggleSelect?: (draggableId: string, shiftKey: boolean) => void;
	multiDragCount?: number;
	onExplode?: (growerName: string) => void;
}) {
	const draggableId = makeGrowerDraggableId(grower.name);

	return (
		<Draggable draggableId={draggableId} index={index}>
			{(provided, snapshot) => {
				const isDimmed = !snapshot.isDragging && multiDragCount > 0;
				const showBadge = snapshot.isDragging && multiDragCount > 1;

				return (
					<Paper
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						elevation={snapshot.isDragging ? 4 : 1}
						onClick={(e) => onToggleSelect?.(draggableId, e.shiftKey)}
						sx={{
							p: 1,
							mb: 0.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							position: "relative",
							bgcolor: snapshot.isDragging ? "action.hover" : isSelected ? "primary.50" : "background.paper",
							cursor: "grab",
							"&:active": { cursor: "grabbing" },
							userSelect: "none",
							opacity: isDimmed ? 0.4 : 1,
							transition: "opacity 0.15s",
						}}
					>
						{showBadge && (
							<Chip
								label={multiDragCount}
								color="primary"
								size="small"
								sx={{
									position: "absolute",
									top: -8,
									right: -8,
									height: 22,
									minWidth: 22,
									fontWeight: 700,
									fontSize: "0.75rem",
									zIndex: 1,
								}}
							/>
						)}
						<Checkbox size="small" checked={!!isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
						<Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
							{grower.name}
						</Typography>
						<Chip label={`${grower.jobCount} jobs`} size="small" color="primary" variant="outlined" />
						{onExplode && grower.jobCount > 1 && (
							<PortalTooltip title="Break apart into individual jobs">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										onExplode(grower.name);
									}}
									sx={{ p: 0.25, mr: 0.5 }}
								>
									<CallSplit fontSize="small" />
								</IconButton>
							</PortalTooltip>
						)}
					</Paper>
				);
			}}
		</Draggable>
	);
}
